import * as React from 'react'
import { formatDriverName } from '@/modules/drivers/utils/driverName'
import { FaIcon, faIdBadge } from '@/lib/fa'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'

import EditableTableCell from './EditableTableCell'
import { SelectionGroup } from '@/modules/drivers/context/TableContext'
import { withRouter, RouteComponentProps } from '@/app/navigation/withRouter'
import { TimesheetRowData } from '../../views/Group.types'

interface BonusRewardRowProps extends RouteComponentProps {
  data: TimesheetRowData
  showBonusFields?: boolean
  selectedIds?: SelectionGroup
  isSelectedAny?: boolean
  style?: React.CSSProperties
}

/**
 * Bonus and reward row component (premia i nagroda)
 * Displays driver information with vacation, sick leave, costs, delegations, netto amounts, and rewards
 */
const BonusRewardRow: React.FC<BonusRewardRowProps> = ({
  data,
  showBonusFields = false,
  selectedIds,
  isSelectedAny = false,
  style,
}) => {
  const isChecked = selectedIds?.ids.includes(data.id) ?? false
  const isActive = true // Assuming all timesheet rows are active
  const isDisabled = isSelectedAny && !isActive
  const [isHovered, setIsHovered] = React.useState(false)

  const handleMouseEnter = () => setIsHovered(true)
  const handleMouseLeave = () => setIsHovered(false)

  const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    // Don't trigger if clicking on an input field or its container
    const target = e.target as HTMLElement
    // Check if the click is on an input element or inside an input container
    if (target.tagName === 'INPUT' || target.closest('input')) {
      return
    }
    // Check if the click is on a checkbox
    if (target.closest('label') || target.closest('[role="checkbox"]')) {
      return
    }
    // Check if the click is on a td that contains an input (editable cell)
    const td = target.closest('td')
    if (td && td.querySelector('input')) {
      return
    }
  }

  return (
    <tr
      className={`notches-border--right tw-w-fit tw-cursor-pointer tw-border-b tw-bg-white hover:tw-bg-primary/10 ${isChecked ? 'tw-bg-primary/10 tw-font-medium' : ''} ${!isActive && isChecked ? 'tw-opacity-50' : !isActive ? 'tw-opacity-30' : ''}`}
      style={style}
      onClick={handleRowClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
    

      {/* NAZWISKO I IMIE */}
      <TableTextRow
        icon={
          <FaIcon
            icon={faIdBadge}
            className="tw-h-3 tw-w-3 tw-text-[#1f2937]"
          />
        }
        size="small"
      >
        {formatDriverName(data)}
      </TableTextRow>

      {/* URLOP n/b */}
      <TableTextRow size="small">
        {data.vacationNb || '0,00 / 0,00'}
      </TableTextRow>

      {/* CHOROBOWE n/b */}
      <TableTextRow size="small">
        {data.sickLeaveNb || '0,00/0,00'}
      </TableTextRow>

      {/* KOSZTY */}
      <td className="tw-cursor-pointer tw-px-3 tw-py-2.5 tw-text-content-base tw-opacity-90">
        <div className="tw-flex tw-items-center tw-justify-end tw-text-nowrap tw-text-sm tw-font-medium">
          <EditableTableCell
            value={data.costs || '0,00'}
            onChange={(value) => {
              // TODO: Handle onChange
              console.log('Costs changed:', value)
            }}
            readOnly={false}
          />
        </div>
      </td>

      {/* delegacje kraj. */}
      <td className="tw-cursor-pointer tw-px-3 tw-py-2.5 tw-text-content-base tw-opacity-90">
        <div className="tw-flex tw-items-center tw-justify-end tw-text-nowrap tw-text-sm tw-font-medium">
          <EditableTableCell
            value={data.domesticDelegations || '0,00'}
            onChange={(value) => {
              // TODO: Handle onChange
              console.log('Delegacje changed:', value)
            }}
            readOnly={false}
          />
        </div>
      </td>

      {/* oczekiwane netto */}
      <td className="tw-cursor-pointer tw-border tw-border-[#1f2937] tw-px-3 tw-py-2.5 tw-text-content-base tw-opacity-90">
        <div className="tw-flex tw-items-center tw-justify-end tw-text-nowrap tw-text-sm tw-font-medium">
          <EditableTableCell
            value={data.expectedNetto || '0,00'}
            onChange={(value) => {
              // TODO: Handle onChange
              console.log('Oczekiwane netto changed:', value)
            }}
            readOnly={false}
          />
        </div>
      </td>

      {/* WYLICzone netto */}
      <td className="tw-cursor-pointer tw-px-3 tw-py-2.5 tw-text-content-base tw-opacity-90">
        <div className="tw-flex tw-items-center tw-justify-end tw-text-nowrap tw-text-sm tw-font-medium">
          <EditableTableCell
            value={data.calculatedNetto || '0,00'}
            onChange={(value) => {
              // TODO: Handle onChange
              console.log('Wyliczone netto changed:', value)
            }}
            readOnly={false}
          />
        </div>
      </td>

      {/* NAGRODA (only if showBonusFields) */}
      {showBonusFields && (
        <td className="tw-cursor-pointer tw-px-3 tw-py-2.5 tw-text-content-base tw-opacity-90">
          <div className="tw-flex tw-items-center tw-justify-end tw-text-nowrap tw-text-sm tw-font-medium">
            <EditableTableCell
              value={data.reward || '0,00'}
              onChange={(value) => {
                // TODO: Handle onChange
                console.log('Nagroda changed:', value)
              }}
              readOnly={false}
            />
          </div>
        </td>
      )}

      {/* brutto (łącznie) */}
      <TableTextRow size="small">{data.grossTotal7Day || '0,00'}</TableTextRow>
    </tr>
  )
}

export default withRouter(BonusRewardRow)
